// lib/functions/sprint はAPI・認証（aws-amplify）まで芋づるに読み込むため、
// ここからimportするとwbs-items.ts経由でjestからテストできなくなる。
// SprintStatusは値が固定の文字列enumなので、同じ値をローカルに定義する。
const SPRINT_STATUS = {
  STANDBY: 'STANDBY',
  INPROGRESS: 'INPROGRESS',
  CANCELED: 'CANCELED',
  FINISHED: 'FINISHED',
} as const
type SprintStatus = (typeof SPRINT_STATUS)[keyof typeof SPRINT_STATUS]
import { dayOffsetToDate } from '../shared'
import { demoProject } from './project'
import { demoTeams } from './members'

const buildDemoSprint = ({
  uuid,
  teamUuid,
  teamName,
  num,
  name,
  startDayOffset,
  endDayOffset,
  status,
}: {
  uuid: string
  teamUuid: string
  teamName: string
  num: number
  name: string
  startDayOffset: number
  endDayOffset: number
  status: SprintStatus
}) => ({
  uuid,
  lockVersion: 1,
  revision: 'demo-revision',
  children: [] as any[],
  teamUuid,
  teamName,
  projectUuid: demoProject.uuid,
  num,
  name,
  startDate: dayOffsetToDate(startDayOffset),
  endDate: dayOffsetToDate(endDayOffset),
  status,
  businessDate: 10,
  businessHour: 80,
})

// スプリントは2週間区切り。team側は現在進行中（INPROGRESS）のスプリントが
// day 0（今週）を含むよう、weekOffsetでチームごとの開始タイミングをずらす。
const buildDemoSprintsForTeam = ({
  team,
  uuidPrefix,
  namePrefix,
  weekOffset,
}: {
  team: { uuid: string; displayName: string }
  uuidPrefix: string
  namePrefix: string
  weekOffset: number
}) => {
  const dayOffset = weekOffset * 14
  return [
    buildDemoSprint({
      uuid: `${uuidPrefix}-1-uuid`,
      teamUuid: team.uuid,
      teamName: team.displayName,
      num: 1,
      name: `${namePrefix}1`,
      startDayOffset: -28 + dayOffset,
      endDayOffset: -15 + dayOffset,
      status: SPRINT_STATUS.FINISHED,
    }),
    buildDemoSprint({
      uuid: `${uuidPrefix}-2-uuid`,
      teamUuid: team.uuid,
      teamName: team.displayName,
      num: 2,
      name: `${namePrefix}2`,
      startDayOffset: -14 + dayOffset,
      endDayOffset: -1 + dayOffset,
      status: SPRINT_STATUS.FINISHED,
    }),
    buildDemoSprint({
      uuid: `${uuidPrefix}-3-uuid`,
      teamUuid: team.uuid,
      teamName: team.displayName,
      num: 3,
      name: `${namePrefix}3`,
      startDayOffset: 0 + dayOffset,
      endDayOffset: 13 + dayOffset,
      status: SPRINT_STATUS.INPROGRESS,
    }),
    buildDemoSprint({
      uuid: `${uuidPrefix}-4-uuid`,
      teamUuid: team.uuid,
      teamName: team.displayName,
      num: 4,
      name: `${namePrefix}4`,
      startDayOffset: 14 + dayOffset,
      endDayOffset: 27 + dayOffset,
      status: SPRINT_STATUS.STANDBY,
    }),
    buildDemoSprint({
      uuid: `${uuidPrefix}-5-uuid`,
      teamUuid: team.uuid,
      teamName: team.displayName,
      num: 5,
      name: `${namePrefix}5`,
      startDayOffset: 28 + dayOffset,
      endDayOffset: 41 + dayOffset,
      status: SPRINT_STATUS.STANDBY,
    }),
  ]
}

export const demoSprints = [
  ...buildDemoSprintsForTeam({
    team: demoTeams[0],
    uuidPrefix: 'demo-sprint-team-a',
    namePrefix: 'Aチーム スプリント',
    weekOffset: 0,
  }),
  ...buildDemoSprintsForTeam({
    team: demoTeams[1],
    uuidPrefix: 'demo-sprint-team-b',
    namePrefix: 'Bチーム スプリント',
    weekOffset: 1,
  }),
]

// モジュール読み込み時点のスナップショットにすると、delta/batchで追加した
// スプリントが列フィルターの選択肢に出てこないため、呼び出しごとに現在の
// demoSprintsから算出する。
export const getDemoSprintFilterOptionUuids = (): string[] =>
  demoSprints.map(sprint => sprint.uuid)

export const demoSprintsByUuid: { [uuid: string]: any } = Object.fromEntries(
  demoSprints.map(sprint => [sprint.uuid, sprint])
)

// スプリント画面のGrid登録（sprints/delta/batchハンドラ）が呼ぶ。
// demoSprints配列を直接書き換えてin-memoryで保存されたように見せるため、
// 追加・削除時はdemoSprintsByUuidとの整合も取る必要がある。
export const addDemoSprint = (sprint: any): void => {
  demoSprints.push(sprint)
  demoSprintsByUuid[sprint.uuid] = sprint
}

export const removeDemoSprint = (uuid: string): void => {
  const index = demoSprints.findIndex(sprint => sprint.uuid === uuid)
  if (index >= 0) {
    demoSprints.splice(index, 1)
  }
  delete demoSprintsByUuid[uuid]
}

// 実APIはチーム内の連番を採番する。新規行のnumが未指定のとき、
// 同一チームの最大num+1を割り当てて画面表示上の連番を再現する。
export const nextDemoSprintNum = (teamUuid: string): number => {
  const numsInTeam = demoSprints
    .filter(sprint => sprint.teamUuid === teamUuid)
    .map(sprint => sprint.num)
  return numsInTeam.length === 0 ? 1 : Math.max(...numsInTeam) + 1
}
